import {
  inr, num, fmtDate, words, has, labelled, joinParts,
  Html, Signature, deriveBasics, missingClientFields,
} from "./documentBasics";

/**
 * The quotation — a document of its own, not a page of the proposal.
 *
 * Its editable copy is three sections from /proposal-template/rendered
 * ?doc=quotation: an intro, the terms and a closing note. Everything else on the
 * page — line items, totals, GST, the bank card, the amount in words — is
 * computed from the same quote the proposal prices.
 */
const QuotationDocument = ({ form, quote, template, quoteNo, segmentLabel, paymentStatus = "unpaid" }) => {
  /* A quotation / proforma invoice is unpaid until the client pays — that is the
     default here and on every freshly built one. */
  const isPaid = String(paymentStatus).toLowerCase() === "paid";
  const missing = missingClientFields(form);
  if (missing.length) {
    return (
      <div className="empty">
        <b>Add the prospect’s details to generate the quotation.</b>
        <br /><br />
        Required: {missing.join(", ")}.
      </div>
    );
  }
  if (!quote) return <div className="empty">Fill the pricing inputs to build the quotation.</div>;

  const {
    company, client, seg, audience, today, validUntil, clientPhone, attorneyNumbers,
    isCustom, p, li, months, termLabel, termWord, firstLabel,
    periodDays, grace, termLength, totalDiscPct, showRateRow, perMarkRate, perMarkUnit,
    onboardingSaving, fill, d,
  } = deriveBasics({ form, quote, template, segmentLabel });

  /* ₹/mark/yr for the attorney taper, ₹/mark/mo for corporate per-mark pricing. */
  const rateUnitLabel = perMarkUnit === "yr" ? "/mark/yr" : "/mark/mo";

  /* Three editable blocks, looked up by key. Anything missing simply does not
     print — an empty heading on a client-facing quotation is worse than a gap. */
  const section = (key) => (template?.sections || []).find((x) => x.key === key);
  const intro = section("quotation_intro")?.content?.html;
  const note = section("quotation_note")?.content?.html;
  const termsSection = section("quotation_terms");
  const terms = termsSection?.content?.items || [];

  /* The summary column, exactly as the API settled it. These four are the same
     figures the payment gateway is charged on, so the page must not arrive at
     its own version of them — the older derivations are kept only as a fallback
     for a response that predates the fields. */
  const gstPercent = p.gst_percent ?? 18;
  const subtotal = p.subtotal_term ?? ((p.monthly_subtotal || 0) * months + (p.onboarding || 0));
  const taxable = p.taxable_total ?? ((p.per_term || 0) + (p.onboarding_net || 0));
  const gstTotal = p.gst_total ?? ((p.gst || 0) + (p.onboarding_gst || 0));
  /* Whole-term discount, onboarding included — the two rows below split it, and
     the onboarding half comes from the pair of onboarding figures. */
  const discountTerm = p.discount_term ?? Math.max(0, subtotal - taxable);
  const recurringDiscount = Math.max(0, discountTerm - onboardingSaving);

  /* Bank details are structured now. If the backend could not parse them it
     hands back the raw text instead, and that is printed as typed. */
  const bank = company.bank || null;
  const bankCells = bank
    ? [
        ["Account No.", bank.account_no],
        ["IFSC (RTGS/NEFT)", bank.ifsc],
        ["Account name", bank.account_name || company.legal_name],
        [bank.branch ? "Branch" : "UPI", bank.branch || bank.upi],
      ].filter(([, v]) => has(v))
    : [];

  const CompanyContact = () => (
    /* Each line is assembled from the fields that exist. Printing the JSX
       directly put a literal "null" on a client-facing quotation whenever the
       company profile left something blank. */
    <div className="qp-lines">
      {joinParts([company.city, labelled("CIN", company.cin)])}
      {joinParts([company.city, labelled("CIN", company.cin)]) && <br />}
      {joinParts([labelled("GSTIN", company.gstin), labelled("SAC", company.sac)])}
      {joinParts([labelled("GSTIN", company.gstin), labelled("SAC", company.sac)]) && <br />}
      {joinParts([company.email, company.whatsapp, company.website])}
    </div>
  );

  const QuotationHead = () => (
    <>
      <div className="qp-head">
        <div className="qp-co">
          <div className="qp-logo">{(company.brand_name || "E").charAt(0)}</div>
          <div>
            <h2>{company.legal_name}</h2>
            <div className="qp-sub">{company.brand_name} — AI Trademark Suite</div>
            <CompanyContact />
          </div>
        </div>
        <div className="qp-meta">
          <div className="qp-title">Quotation / Proforma Invoice</div>
          <table><tbody>
            <tr><td>Quotation No</td><td><b>{quoteNo}</b></td></tr>
            <tr><td>Date</td><td><b>{fmtDate(today)}</b></td></tr>
            <tr><td>Valid until</td><td><b>{fmtDate(validUntil)}</b></td></tr>
            <tr>
              <td>Payment status</td>
              <td><span className={`qp-status${isPaid ? " paid" : ""}`}>{isPaid ? "Paid" : "Unpaid"}</span></td>
            </tr>
          </tbody></table>
        </div>
      </div>
      <div className="qp-parties">
        <div className="qp-party">
          <div className="qp-h">Bill to</div>
          <div className="qp-nm">{client}</div>
          <div className="qp-dt">
            {form.contact_name && <>Attn: <b>{form.contact_name}</b><br /></>}
            {form.email}{form.email && clientPhone ? " · " : ""}{clientPhone}
            {form.address && <><br />{form.address}</>}
            {form.gstin && <><br />GSTIN: {form.gstin}</>}
          </div>
        </div>
        <div className="qp-party">
          <div className="qp-h">Engagement</div>
          <div className="qp-dt">
            {audience} · <b>{num(quote.marks)}</b> trademarks · {form.users || 1} user(s)<br />
            Plan: <b>{quote.plan}</b> · billed {termWord}ly · 12-month term
            {attorneyNumbers && <><br />Attorney numbers: <b>{attorneyNumbers}</b></>}
          </div>
        </div>
      </div>
      {intro && (
        <div className="qp-sec" style={{ paddingBottom: 0 }}>
          <Html as="div" style={{ fontSize: 11.5, color: "#3F4F65", lineHeight: 1.6 }} html={fill(intro)} />
        </div>
      )}
    </>
  );

  const QuotationTerms = () => (
    <>
      <div className="qp-terms">
        <h3>{termsSection?.title || "Terms & Conditions"}</h3>
        <ol>{terms.map((t, i) => <Html as="li" key={i} html={fill(t)} />)}</ol>
      </div>
      <div className="qp-sign">
        <div className="qp-sigL"><div className="qp-sigbox" />Client acceptance — signature, name &amp; date</div>
        <div className="qp-sigL">
          {/* Same seal as the proposal, so the two documents cannot disagree
              about who signed them. */}
          <div className="qp-sigbox">
            <Signature
              sealId="qseal" imageUrl={company.signature_url}
              legalName={company.legal_name} city={company.city}
            />
          </div>
          For <b>{company.legal_name}</b><br />
          <b>{company.signatory_name}</b> · {company.signatory_role}
        </div>
      </div>
    </>
  );

  return (
    <div className={`theme-${seg}`}>
      <div className="page">
        <QuotationHead />
        {isCustom ? (
          <>
            <div className="qp-sec">
              <table className="qp-items"><tbody>
                <tr><th>#</th><th>Description</th><th className="r">Amount</th></tr>
                <tr>
                  <td>1</td>
                  <td>
                    <b>{company.brand_name} Enterprise — custom engagement</b><br />
                    <span className="qp-mut">{audience} · {num(quote.marks)} trademarks</span>
                  </td>
                  <td className="r">On request</td>
                </tr>
              </tbody></table>
            </div>
            <div className="qp-note">
              {quote.message || "Quoted as a custom Enterprise engagement; our team will share tailored commercials."}
            </div>
          </>
        ) : (
          <>
            <div className="qp-sec">
              <table className="qp-items"><tbody>
                <tr>
                  <th>#</th><th>Description</th><th className="r">Qty (mo)</th>
                  <th className="r">Rate</th><th className="r">Amount</th>
                </tr>
                <tr>
                  <td>1</td>
                  <td>
                    <b>{company.brand_name} {quote.plan} subscription</b><br />
                    <span className="qp-mut">
                      {audience} · {num(quote.marks)} TMs · {form.users || 1} user(s)
                      {showRateRow && ` · ₹${num(perMarkRate)}${rateUnitLabel}`}
                      {has(company.sac) ? ` · SAC ${company.sac}` : ""} · {termLabel} billing
                    </span>
                  </td>
                  <td className="r">{months}</td>
                  <td className="r">{inr(li.base_monthly)}</td>
                  <td className="r">{inr((li.base_monthly || 0) * months)}</td>
                </tr>
                {li.addons_monthly > 0 && (
                  <tr>
                    <td>2</td>
                    <td>
                      <b>Add-ons</b><br />
                      <span className="qp-mut">
                        {li.extra_users ? `${li.extra_users} extra ${li.free_clients !== undefined && li.free_clients !== null ? "seat(s)" : "user(s)"}` : ""}
                        {li.extra_clients ? ` · ${li.extra_clients} extra client(s)` : ""}
                      </span>
                    </td>
                    <td className="r">{months}</td>
                    <td className="r">{inr(li.addons_monthly)}</td>
                    <td className="r">{inr(li.addons_monthly * months)}</td>
                  </tr>
                )}
                {p.onboarding > 0 && (
                  <tr>
                    <td>{li.addons_monthly > 0 ? 3 : 2}</td>
                    <td>
                      <b>Onboarding &amp; setup</b> (one-time)
                      {d.onboarding_percent > 0 && <span className="qp-grn"> — {d.onboarding_percent}% off</span>}
                      <br /><span className="qp-mut">Portfolio import, configuration &amp; training</span>
                    </td>
                    <td className="r">1</td>
                    <td className="r">{inr(p.onboarding)}</td>
                    <td className="r">{inr(p.onboarding_net)}</td>
                  </tr>
                )}
              </tbody></table>
            </div>

            <div className="qp-totals">
              <div className="qp-pay">
                <div className="qp-h">Bank &amp; payment details</div>
                {bank ? (
                  <div className="bankcard">
                    <div className="bank-top">
                      <span className="bank-ic">🏦</span>
                      <div>
                        <div className="bank-nm">{bank.bank_name || "Bank & payment details"}</div>
                        <div className="bank-sub">
                          A/C in favour of {bank.account_name || company.legal_name}
                        </div>
                      </div>
                    </div>
                    {/* Only the cells that have a value — an empty "UPI —" row
                        reads as a detail somebody forgot rather than one that
                        does not apply. */}
                    {bankCells.length > 0 && (
                      <div className="bank-grid">
                        {bankCells.map(([bl, bv]) => (
                          <div className="bcell" key={bl}>
                            <span className="bl">{bl}</span>
                            <span className="bv">{bv}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : has(company.account_details) ? (
                  /* Fallback for a profile whose details were never structured:
                     printed as typed, line breaks and all. */
                  <div className="bankcard">
                    <div className="bank-top">
                      <span className="bank-ic">🏦</span>
                      <div>
                        <div className="bank-nm">Bank &amp; payment details</div>
                        <div className="bank-sub">A/C in favour of {company.legal_name}</div>
                      </div>
                    </div>
                    <div style={{ padding: "10px 12px", fontSize: 11, color: "#0A0D14", lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word", fontVariantNumeric: "tabular-nums" }}>
                      {company.account_details}
                    </div>
                  </div>
                ) : null}
                <div className="qp-terminfo">
                  Term <b>{termLength}</b> · billing every {periodDays} days ·
                  {" "}{grace}-day grace.<br />
                  Recurring <b>{inr(p.term_invoice)}</b>/{termWord} (incl. GST) · est. annual value{" "}
                  <b>{inr(p.annual_value)}</b> + GST.
                </div>
              </div>

              <table className="qp-sum"><tbody>
                <tr>
                  {/* No onboarding charge, no onboarding in the label — the row
                      above it would not be there either. */}
                  <td>Subtotal ({firstLabel}{p.onboarding > 0 ? " + onboarding" : ""})</td>
                  <td className="r">{inr(subtotal)}</td>
                </tr>
                {recurringDiscount > 0 && (
                  <tr className="grn">
                    <td>Discount ({totalDiscPct}%)</td>
                    <td className="r">− {inr(recurringDiscount)}</td>
                  </tr>
                )}
                {onboardingSaving > 0 && (
                  <tr className="grn">
                    <td>Onboarding discount ({d.onboarding_percent}%)</td>
                    <td className="r">− {inr(onboardingSaving)}</td>
                  </tr>
                )}
                <tr><td>Taxable value</td><td className="r">{inr(taxable)}</td></tr>
                <tr><td>IGST @ {gstPercent}%</td><td className="r">{inr(gstTotal)}</td></tr>
                <tr className="grand">
                  <td>Grand Total ({firstLabel} payable)</td>
                  <td className="r">{inr(p.first_invoice || p.term_invoice)}</td>
                </tr>
              </tbody></table>
            </div>

            <div className="qp-words">
              <b>Amount in words:</b> Rupees {words(p.first_invoice || p.term_invoice)} Only.
            </div>
            {note && (
              <Html as="div" className="qp-note" html={fill(note)} />
            )}
          </>
        )}
        <QuotationTerms />
      </div>
    </div>
  );
};

export default QuotationDocument;
